<script setup lang="ts">
// ビューポート。WebGL キャンバス (合成表示 + マーチングアンツ) + 2D オーバーレイ (ラバーバンド等)。
// ポインタ操作: 手のひら/中ボタン = パン、ホイール = パン、Ctrl/Cmd+ホイール = ズーム、
// 移動ツール = アクティブレイヤーのドラッグ、選択ツール = 長方形/楕円/なげなわ/多角形/自動選択。
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore, type SelectionMode } from '@/stores/uiStore'
import { mat3Invert as penInvert } from '@/pixels/transform'
import { mat3Apply as penApply, homographyFromQuad, MAT3_IDENTITY, rectIntersect } from '@/types/geometry'
import { initWarpControls, warpIsIdentity, warpImageBezier, bezierPatch } from '@/pixels/meshWarp'
import type { RgbaImage } from '@/pixels/transform'
import { startEngine, stopEngine, requestFrame, readComposite, beginStroke, addStrokeSample, endStroke, endQuickMaskStroke, finishStrokeForHeal, setLiveImagePreview, clearLiveImagePreview, cancelStroke, sampleComposite, setLiveTransform } from '@/app/engine'
import { mat3Multiply, mat3Translate, mat3Scale, mat3Apply, type Mat3 } from '@/types/geometry'
import { hexToRgb, rgbToHex, rgbToUnit } from '@/color/convert'
import { maskToAlpha, solidWithAlpha, renderGradient } from '@/brush/fill'
import { combineMasks } from '@/selection/mask'
import { toast } from '@/composables/useToast'
import { openFiles, openFilePicker } from '@/app/actions'
import { useI18n } from '@/i18n'
import type { Pt, Rect } from '@/types/geometry'
import { rasterizeRect, rasterizeEllipse, rasterizePolygon, type MaskData } from '@/selection/mask'
import { featherMask } from '@/selection/modify'
import { magicWand } from '@/selection/floodFill'
import { magicWandAsync } from '@/workers/clients'
import { buildQuickProxy, quickSelectDab, quickMaskToFull, type QuickProxy } from '@/selection/quickSelect'
import { retouchAsync } from '@/workers/clients'
import { smudgeStroke } from '@/retouch/smudge'
import { liveWire, simplifyPath } from '@/selection/magnetic'
import { useBrushStore } from '@/brush/presets'
import type { StrokeSample } from '@/brush/dabs'
import { pixelStore } from '@/pixels/pixelStore'
import { snapTargets, snapDelta } from '@/model/snap'

const { t } = useI18n()
const docStore = useDocumentStore()
const ui = useUiStore()
const host = ref<HTMLDivElement | null>(null)
const textArea = ref<HTMLTextAreaElement | null>(null)
const RULER = 18
const rulerSize = computed(() => (ui.showRulers && docStore.hasDocument ? RULER : 0))
const rulerX = ref<HTMLCanvasElement | null>(null)
const rulerY = ref<HTMLCanvasElement | null>(null)

function drawRulers() {
  if (!rulerSize.value || !docStore.doc) return
  const dpr = window.devicePixelRatio || 1
  const z = ui.view.zoom
  // 目盛りの間隔: 文書 px で 10/25/50/100/250/500/1000
  const steps = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000]
  const major = steps.find(st => st * z >= 60) ?? 5000
  const minor = major / 5
  const fg = getComputedStyle(document.documentElement).getPropertyValue('--fg-2').trim() || '#888'
  const line = getComputedStyle(document.documentElement).getPropertyValue('--line-strong').trim() || '#555'
  const paint = (c: HTMLCanvasElement | null, horizontal: boolean) => {
    if (!c) return
    const W = horizontal ? ui.viewportSize.w : RULER, H = horizontal ? RULER : ui.viewportSize.h
    if (W <= 0 || H <= 0) return
    c.width = Math.round(W * dpr)
    c.height = Math.round(H * dpr)
    const ctx = c.getContext('2d')!
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, W, H)
    ctx.strokeStyle = line
    ctx.fillStyle = fg
    ctx.font = '9px ' + getComputedStyle(document.documentElement).getPropertyValue('--font-mono')
    ctx.lineWidth = 1
    const len = horizontal ? W : H
    const pan = horizontal ? ui.view.pan.x : ui.view.pan.y
    const startDoc = Math.floor(-pan / z / minor) * minor
    const endDoc = (len - pan) / z
    ctx.beginPath()
    for (let v = startDoc; v <= endDoc; v += minor) {
      const s = Math.round(v * z + pan) + 0.5
      const isMajor = Math.abs(v / major - Math.round(v / major)) < 1e-6
      const tick = isMajor ? RULER - 4 : RULER - 10
      if (horizontal) { ctx.moveTo(s, RULER); ctx.lineTo(s, RULER - tick) }
      else { ctx.moveTo(RULER, s); ctx.lineTo(RULER - tick, s) }
      if (isMajor) {
        if (horizontal) ctx.fillText(String(Math.round(v)), s + 2, 9)
        else {
          ctx.save()
          ctx.translate(9, s - 2)
          ctx.rotate(-Math.PI / 2)
          ctx.fillText(String(Math.round(v)), 0, 0)
          ctx.restore()
        }
      }
    }
    ctx.stroke()
    // カーソル位置
    if (ui.cursorDocPos) {
      const s = Math.round((horizontal ? ui.cursorDocPos.x : ui.cursorDocPos.y) * z + pan) + 0.5
      ctx.strokeStyle = '#19c3f5'
      ctx.beginPath()
      if (horizontal) { ctx.moveTo(s, 0); ctx.lineTo(s, RULER) } else { ctx.moveTo(0, s); ctx.lineTo(RULER, s) }
      ctx.stroke()
    }
  }
  paint(rulerX.value, true)
  paint(rulerY.value, false)
}
watch([() => ui.view, () => ui.viewportSize, rulerSize, () => ui.cursorDocPos, () => docStore.sessionVersion], () => drawRulers(), { deep: true })
const glCanvas = ref<HTMLCanvasElement | null>(null)
const overlay = ref<HTMLCanvasElement | null>(null)
const dragging = ref(false)
const dropActive = ref(false)

let ro: ResizeObserver | null = null

onMounted(() => {
  const el = host.value!
  ro = new ResizeObserver(() => {
    const r = el.getBoundingClientRect()
    const first = ui.viewportSize.w === 0
    ui.viewportSize = { w: Math.round(r.width) - rulerSize.value, h: Math.round(r.height) - rulerSize.value }
    if (first && docStore.doc) ui.fitToViewport(docStore.doc.meta.width, docStore.doc.meta.height)
    drawOverlay()
  })
  ro.observe(el)
  const r = el.getBoundingClientRect()
  ui.viewportSize = { w: Math.round(r.width) - rulerSize.value, h: Math.round(r.height) - rulerSize.value }
  if (glCanvas.value) startEngine(glCanvas.value)
  if (docStore.doc) ui.fitToViewport(docStore.doc.meta.width, docStore.doc.meta.height)
  window.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  ro?.disconnect()
  stopEngine()
  window.removeEventListener('keydown', onKeyDown)
})

watch(
  () => docStore.sessionVersion,
  () => {
    if (docStore.doc) ui.fitToViewport(docStore.doc.meta.width, docStore.doc.meta.height)
    requestFrame(true)
  }
)
watch(() => ui.view, () => drawOverlay(), { deep: true })
watch([() => ui.showGrid, () => ui.showGuides, () => ui.gridSize, () => docStore.doc?.guides, () => ui.draggingGuide], () => drawOverlay())
watch(rulerSize, () => {
  const el = host.value
  if (!el) return
  const r = el.getBoundingClientRect()
  ui.viewportSize = { w: Math.round(r.width) - rulerSize.value, h: Math.round(r.height) - rulerSize.value }
  drawOverlay()
})

function localPos(e: PointerEvent | WheelEvent | MouseEvent): Pt {
  const r = host.value!.getBoundingClientRect()
  return { x: e.clientX - r.left - rulerSize.value, y: e.clientY - r.top - rulerSize.value }
}

// ---------- ガイド (定規からドラッグ / 既存ガイドのドラッグ) ----------
function hitGuide(css: Pt): { id: string; axis: 'x' | 'y' } | null {
  const d = docStore.doc
  if (!d || !ui.showGuides) return null
  for (const g of d.guides) {
    const s = g.axis === 'x' ? ui.docToCss({ x: g.position, y: 0 }).x : ui.docToCss({ x: 0, y: g.position }).y
    if (Math.abs((g.axis === 'x' ? css.x : css.y) - s) <= 4) return { id: g.id, axis: g.axis }
  }
  return null
}
function onRulerDown(axis: 'x' | 'y', e: PointerEvent) {
  if (!docStore.doc) return
  e.preventDefault()
  const move = (ev: PointerEvent) => {
    const p = localPos(ev)
    const doc = ui.cssToDoc(p)
    ui.draggingGuide = { axis, position: Math.round(axis === 'x' ? doc.x : doc.y) }
  }
  const up = (ev: PointerEvent) => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    const p = localPos(ev)
    const doc = ui.cssToDoc(p)
    const g = ui.draggingGuide
    ui.draggingGuide = null
    // ビューポート内で離したら追加
    if (g && p.x >= 0 && p.y >= 0 && p.x <= ui.viewportSize.w && p.y <= ui.viewportSize.h) docStore.addGuide(axis, axis === 'x' ? doc.x : doc.y)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}

type Drag =
  | { kind: 'pan'; last: Pt }
  | { kind: 'stroke'; label: string; quickMask?: { subtract: boolean; opacity: number }; heal?: 'spot' | 'brush' }
  | { kind: 'gradient'; start: Pt; current: Pt }
  | { kind: 'shape'; start: Pt; current: Pt; shift: boolean }
  | { kind: 'guide'; id: string; axis: 'x' | 'y' }
  | { kind: 'xform-move'; start: Pt; origin: Pt }
  | { kind: 'xform-scale'; handle: number; start: Pt; startScale: { x: number; y: number }; startT: Pt; anchorWorld: Pt; local: Pt }
  | { kind: 'xform-rotate'; startAngle: number; startPointer: number }
  | { kind: 'xform-corner'; corner: number; start: Pt; startQuad: [Pt, Pt, Pt, Pt] }
  | { kind: 'xform-warp'; index: number; start: Pt; origin: Pt }
  | { kind: 'smudge'; layerId: string; samples: StrokeSample[] }
  | { kind: 'crop-new'; start: Pt }
  | { kind: 'crop-move'; start: Pt; origin: Rect }
  | { kind: 'crop-resize'; handle: number; origin: Rect }
  | { kind: 'move'; layerId: string; start: Pt; origin: Pt }
  | { kind: 'marquee'; start: Pt; current: Pt; mode: SelectionMode; shift: boolean }
  | { kind: 'lasso'; points: Pt[]; mode: SelectionMode }
  | { kind: 'pen-new'; index: number; start: Pt }
  | { kind: 'quick'; proxy: QuickProxy; base: MaskData | null; mode: SelectionMode; subtract: boolean; lastLive: number; last: Pt }
  | { kind: 'pen-edit'; layerId: string; index: number; part: 'anchor' | 'in' | 'out'; start: Pt; orig: PenPoint[]; local: Pt }

let drag: Drag | null = null

// ---------- ペンツール ----------
interface PenPoint { x: number; y: number; in?: Pt; out?: Pt }
/** 作成中のパス (文書座標) */
const pen = ref<{ points: PenPoint[]; hover: Pt | null; closed: boolean } | null>(null)
/** 編集対象: ペンツール中でアクティブなパス型シェイプ */
function penEditTarget() {
  const l = docStore.activeLayer
  return l && l.kind === 'shape' && l.geometry.type === 'path' && !l.lock.all ? l : null
}
function clonePoints(pts: PenPoint[]): PenPoint[] {
  return pts.map(p => ({ x: p.x, y: p.y, ...(p.in ? { in: { ...p.in } } : {}), ...(p.out ? { out: { ...p.out } } : {}) }))
}
function startPen(doc: Pt) {
  pen.value = { points: [{ x: doc.x, y: doc.y }], hover: null, closed: false }
  ui.modalTool = 'pen'
}
function finishPen(commit: boolean) {
  const p = pen.value
  pen.value = null
  if (ui.modalTool === 'pen') ui.modalTool = null
  drawOverlay()
  if (!commit || !p || p.points.length < 2) return
  const o = ui.toolOptions
  const fill = p.closed && o.shapeFill ? ui.foreground : null
  const stroke = o.shapeStroke ? ui.background : fill ? null : ui.foreground
  docStore.addShapeLayer({ type: 'path', points: clonePoints(p.points), closed: p.closed }, { fillHex: fill, strokeHex: stroke, strokeWidth: o.shapeStrokeWidth, name: 'パス' })
}
/** 既存パスのアンカー / ハンドルのヒットテスト (css 座標) */
function hitPenEdit(css: Pt): { index: number; part: 'anchor' | 'in' | 'out' } | null {
  const l = penEditTarget()
  if (!l || l.geometry.type !== 'path') return null
  const th = 6
  let best: { index: number; part: 'anchor' | 'in' | 'out'; d: number } | null = null
  l.geometry.points.forEach((pt, i) => {
    const cands: { part: 'anchor' | 'in' | 'out'; p: Pt }[] = [{ part: 'anchor', p: pt }]
    if (pt.in) cands.push({ part: 'in', p: pt.in })
    if (pt.out) cands.push({ part: 'out', p: pt.out })
    for (const c of cands) {
      const q = ui.docToCss(penApply(l.transform, c.p))
      const d = Math.hypot(q.x - css.x, q.y - css.y)
      if (d <= th && (!best || d < best.d)) best = { index: i, part: c.part, d }
    }
  })
  return best
}
function penEditMove(d: Extract<Drag, { kind: 'pen-edit' }>, doc: Pt, alt: boolean) {
  const l = docStore.doc?.layers[d.layerId]
  if (!l || l.kind !== 'shape' || l.geometry.type !== 'path') return
  const inv = penInvert(l.transform)
  if (!inv) return
  const cur = penApply(inv, doc)
  const dx = cur.x - d.local.x, dy = cur.y - d.local.y
  const pts = clonePoints(d.orig)
  const pt = pts[d.index]!
  const o = d.orig[d.index]!
  if (d.part === 'anchor') {
    pt.x = o.x + dx; pt.y = o.y + dy
    if (o.in) pt.in = { x: o.in.x + dx, y: o.in.y + dy }
    if (o.out) pt.out = { x: o.out.x + dx, y: o.out.y + dy }
  } else {
    const h = d.part === 'in' ? o.in! : o.out!
    const nh = { x: h.x + dx, y: h.y + dy }
    if (d.part === 'in') pt.in = nh
    else pt.out = nh
    if (!alt) {
      // 反対側のハンドルを鏡像に (Alt で折れ点)
      const mirror = { x: o.x - (nh.x - o.x), y: o.y - (nh.y - o.y) }
      if (d.part === 'in') pt.out = mirror
      else pt.in = mirror
    }
  }
  docStore.setShape(d.layerId, { geometry: { type: 'path', points: pts, closed: l.geometry.closed } }, 'history.editPath')
}

// ---------- 自由変形 ----------
interface TransformSession {
  layerId: string
  /** 変形前の内容矩形 (文書座標) */
  src: Rect
  scaleX: number
  scaleY: number
  angle: number
  /** 中心の移動量 */
  t: Pt
  mode: 'free' | 'distort' | 'perspective' | 'warp'
  /** distort / perspective のときの 4 隅 (文書座標: 左上, 右上, 右下, 左下) */
  quad: [Pt, Pt, Pt, Pt] | null
  /** warp のときの 4×4 制御点 (文書座標、行優先) */
  warp: Pt[] | null
}
const xform = ref<TransformSession | null>(null)

function xformMatrix(x: TransformSession): Mat3 {
  if (x.quad) return homographyFromQuad(x.src, x.quad) ?? MAT3_IDENTITY
  const cx = x.src.x + x.src.w / 2, cy = x.src.y + x.src.h / 2
  const c = Math.cos(x.angle), sn = Math.sin(x.angle)
  const rot: Mat3 = [c, sn, 0, -sn, c, 0, 0, 0, 1]
  return mat3Multiply(mat3Translate(cx + x.t.x, cy + x.t.y), mat3Multiply(rot, mat3Multiply(mat3Scale(x.scaleX, x.scaleY), mat3Translate(-cx, -cy))))
}
function xformCorners(x: TransformSession): Pt[] {
  const m = xformMatrix(x)
  const r = x.src
  const pts = [
    { x: r.x, y: r.y }, { x: r.x + r.w / 2, y: r.y }, { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h / 2 }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x + r.w / 2, y: r.y + r.h },
    { x: r.x, y: r.y + r.h }, { x: r.x, y: r.y + r.h / 2 }
  ]
  return pts.map(p => mat3Apply(m, p))
}
function beginTransform() {
  const d = docStore.doc
  if (!d) return
  const id = d.activeLayerIds[0]
  const anyLayer = id ? d.layers[id] : undefined
  const layer = anyLayer && (anyLayer.kind === 'raster' || anyLayer.kind === 'shape' || anyLayer.kind === 'smart') ? anyLayer : undefined
  if (!layer || layer.lock.all || layer.lock.pixels) return
  const rect = docStore.layerRect(layer.id)
  if (!rect) return
  if (crop.value) cancelCrop()
  xform.value = { layerId: layer.id, src: rect, scaleX: 1, scaleY: 1, angle: 0, t: { x: 0, y: 0 }, mode: 'free', quad: null, warp: null }
  ui.modalTool = 'transform'
  ui.transformState = { scaleX: 1, scaleY: 1, angle: 0, mode: 'free', canDistort: layer.kind === 'raster' }
  pushLiveTransform()
}
function pushLiveTransform() {
  const x = xform.value
  if (!x) return
  ui.transformState = { scaleX: x.scaleX, scaleY: x.scaleY, angle: x.angle, mode: x.mode, canDistort: docStore.doc?.layers[x.layerId]?.kind === 'raster', quad: x.quad ? x.quad.map(q => ({ ...q })) : null, warp: x.warp ? x.warp.map(q => ({ ...q })) : null }
  if (x.warp) {
    setLiveTransform(null)
    scheduleWarpPreview()
  } else setLiveTransform(x.layerId, xformMatrix(x))
  drawOverlay()
}
function commitTransform() {
  const x = xform.value
  if (!x) return
  xform.value = null
  ui.modalTool = null
  ui.transformState = null
  setLiveTransform(null)
  clearLiveImagePreview(false)
  if (x.warp) {
    if (!warpIsIdentity(x.src, x.warp)) {
      const r = warpLayerImage(x.layerId, x.warp, 32)
      if (r) docStore.replaceLayerImage(x.layerId, r.image, r.rect, 'history.warp')
    }
    drawOverlay()
    return
  }
  const changed = !!x.quad || x.scaleX !== 1 || x.scaleY !== 1 || x.angle !== 0 || x.t.x !== 0 || x.t.y !== 0
  if (changed) {
    // 平行移動だけなら再サンプルせずオフセットを動かす
    if (!x.quad && x.scaleX === 1 && x.scaleY === 1 && x.angle === 0) {
      const l = docStore.rasterLayer(x.layerId)
      if (l) docStore.setLayerOffset(x.layerId, { x: l.offset.x + x.t.x, y: l.offset.y + x.t.y })
    } else {
      docStore.transformLayer(x.layerId, xformMatrix(x), 'bicubic')
    }
  }
  drawOverlay()
}
function cancelTransform() {
  xform.value = null
  ui.modalTool = null
  ui.transformState = null
  setLiveTransform(null)
  clearLiveImagePreview()
  drawOverlay()
}
/** ワープ: レイヤーの内容を制御点でワープした画像 (文書座標) */
function warpLayerImage(layerId: string, ctrl: Pt[], subdiv: number): { image: RgbaImage; rect: Rect } | null {
  const l = docStore.rasterLayer(layerId)
  const s = l ? pixelStore.get(l.pixelId) : undefined
  const b = s?.contentBounds()
  if (!l || !s || !b) return null
  const img: RgbaImage = { width: b.w, height: b.h, data: s.readRect(b) }
  return warpImageBezier(img, { x: b.x + l.offset.x, y: b.y + l.offset.y, w: b.w, h: b.h }, ctrl, subdiv)
}
let warpTimer: number | null = null
function scheduleWarpPreview() {
  if (warpTimer !== null) return
  warpTimer = window.setTimeout(() => {
    warpTimer = null
    const x = xform.value
    if (!x || !x.warp) return
    // プレビューは粗め (分割 12)。確定時に 32 で描き直す
    const r = warpLayerImage(x.layerId, x.warp, 12)
    if (r) setLiveImagePreview(x.layerId, r.image.data, r.rect)
  }, 40)
}
function hitWarpPoint(css: Pt): number | null {
  const x = xform.value
  if (!x?.warp) return null
  let best: { i: number; d: number } | null = null
  x.warp.forEach((p, i) => {
    const c = ui.docToCss(p)
    const d = Math.hypot(c.x - css.x, c.y - css.y)
    if (d <= 7 && (!best || d < best.d)) best = { i, d }
  })
  return best ? (best as { i: number }).i : null
}
watch(() => ui.transformRequest, () => beginTransform())
watch(
  () => ui.transformState && [ui.transformState.scaleX, ui.transformState.scaleY, ui.transformState.angle, ui.transformState.mode],
  v => {
    const x = xform.value
    if (!x || !v) return
    const sx = (v[0] as number) ?? x.scaleX, sy = (v[1] as number) ?? x.scaleY, a = (v[2] as number) ?? x.angle
    const mode0 = (v[3] as TransformSession['mode']) ?? x.mode
    if (sx === x.scaleX && sy === x.scaleY && a === x.angle && mode0 === x.mode) return
    if (Number.isFinite(sx) && sx !== 0) x.scaleX = sx
    if (Number.isFinite(sy) && sy !== 0) x.scaleY = sy
    if (Number.isFinite(a)) x.angle = a
    const mode = ui.transformState?.mode ?? 'free'
    if (mode !== x.mode) {
      const wasWarp = x.mode === 'warp'
      x.mode = mode
      if (mode === 'warp') {
        // 現在の変形 (行列) を通した 4×4 格子を制御点にする
        const m = xformMatrix(x)
        x.warp = initWarpControls(x.src, p => penApply(m, p))
      } else {
        if (wasWarp) {
          x.warp = null
          clearLiveImagePreview()
        }
        if (mode === 'free') x.quad = null
        else if (!x.quad) {
          const c = xformCorners(x)
          x.quad = [c[0]!, c[2]!, c[4]!, c[6]!]
        }
      }
    }
    pushLiveTransform()
  }
)
watch(
  () => ui.modalToolCommand,
  cmd => {
    if (!cmd) return
    ui.modalToolCommand = null
    if (ui.modalTool === 'transform') cmd === 'commit' ? commitTransform() : cancelTransform()
    else if (ui.modalTool === 'crop') cmd === 'commit' ? commitCrop() : cancelCrop()
    else if (ui.modalTool === 'text') cmd === 'commit' ? finishTextEdit() : cancelTextEdit()
    else if (ui.modalTool === 'pen') magnet.value ? finishMagnet(cmd === 'commit') : finishPen(cmd === 'commit')
  }
)
/** 変形ハンドルのヒットテスト: 0..7 = ハンドル、'inside'、'rotate'、null */
function hitTransform(css: Pt): number | 'inside' | 'rotate' | null {
  const x = xform.value
  if (!x) return null
  const corners = xformCorners(x).map(p => ui.docToCss(p))
  for (let i = 0; i < 8; i++) {
    if (x.quad && i % 2 === 1) continue
    const c = corners[i]!
    if (Math.abs(css.x - c.x) <= 6 && Math.abs(css.y - c.y) <= 6) return i
  }
  // 内側判定 (逆変換して src 矩形内か)
  const doc = ui.cssToDoc(css)
  const inv = invertMat(xformMatrix(x))
  if (inv) {
    const p = mat3Apply(inv, doc)
    if (p.x >= x.src.x && p.x <= x.src.x + x.src.w && p.y >= x.src.y && p.y <= x.src.y + x.src.h) return 'inside'
  }
  // 角の外側 24px 以内なら回転
  for (const i of [0, 2, 4, 6]) {
    const c = corners[i]!
    if (Math.hypot(css.x - c.x, css.y - c.y) <= 28) return 'rotate'
  }
  return null
}
function invertMat(m: Mat3): Mat3 | null {
  const [a, b, c, d, e, f, g, h, i] = m
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g
  const det = a * A + b * B + c * C
  if (Math.abs(det) < 1e-12) return null
  const k = 1 / det
  return [A * k, -(b * i - c * h) * k, (b * f - c * e) * k, B * k, (a * i - c * g) * k, -(a * f - c * d) * k, C * k, -(a * h - b * g) * k, (a * e - b * d) * k]
}

// ---------- テキスト編集 ----------
const editing = computed(() => {
  const id = ui.editingTextId
  const l = id ? docStore.doc?.layers[id] : undefined
  return l && l.kind === 'text' ? l : null
})
const textAreaStyle = computed(() => {
  const l = editing.value
  if (!l) return {}
  const z = ui.view.zoom
  const st = l.style
  const p = ui.docToCss(l.position)
  const vertical = st.orientation === 'vertical'
  const base: Record<string, string> = {
    left: `${p.x}px`,
    top: `${p.y - st.fontSize * z}px`,
    fontFamily: st.fontFamily,
    fontSize: `${st.fontSize * z}px`,
    fontWeight: String(st.fontWeight),
    fontStyle: st.italic ? 'italic' : 'normal',
    lineHeight: String(st.lineHeight),
    letterSpacing: `${st.letterSpacing * z}px`,
    color: st.hex,
    textAlign: st.align,
    width: l.boxWidth ? `${l.boxWidth * z}px` : 'auto',
    minWidth: vertical ? `${st.fontSize * z * 1.5}px` : `${st.fontSize * z * 2}px`
  }
  if (st.align === 'center') base.transform = 'translateX(-50%)'
  else if (st.align === 'right') base.transform = 'translateX(-100%)'
  if (vertical) {
    base.writingMode = 'vertical-rl'
    base.top = `${p.y}px`
    base.left = `${p.x - st.fontSize * z * 0.6}px`
    base.transform = 'translateX(-100%)'
  }
  return base
})
function startTextEdit(id: string) {
  if (ui.editingTextId === id) return
  finishTextEdit()
  ui.editingTextId = id
  ui.modalTool = 'text'
  docStore.beginTransaction('history.editText')
  const focusIt = () => {
    const ta = textArea.value
    if (!ta || document.activeElement === ta) return
    ta.focus()
    if (ta.value.length) ta.select()
  }
  void nextTick(focusIt)
  requestAnimationFrame(focusIt)
  setTimeout(focusIt, 50)
}
function onTextInput(e: Event) {
  const l = editing.value
  if (!l) return
  const v = (e.target as HTMLTextAreaElement).value
  docStore.setText(l.id, { text: v })
}
function finishTextEdit() {
  const l = editing.value
  ui.editingTextId = null
  if (ui.modalTool === 'text') ui.modalTool = null
  if (!l) return
  docStore.commitTransaction()
  // 空のままなら削除
  const cur = docStore.doc?.layers[l.id]
  if (cur && cur.kind === 'text' && cur.text.trim() === '') docStore.removeLayers([l.id])
}
function cancelTextEdit() {
  const l = editing.value
  ui.editingTextId = null
  if (ui.modalTool === 'text') ui.modalTool = null
  if (!l) return
  docStore.rollbackTransaction()
  const cur = docStore.doc?.layers[l.id]
  if (cur && cur.kind === 'text' && cur.text.trim() === '') docStore.removeLayers([l.id])
}
function onTextKey(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault()
    finishTextEdit()
  } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault()
    finishTextEdit()
  }
  e.stopPropagation()
}
/** テキストツールでクリック: 既存テキストなら編集、なければ新規作成 */
function textToolClick(doc: Pt) {
  const d = docStore.doc!
  // 既存テキストのヒットテスト (上から)
  for (const id of [...Object.keys(d.layers)].reverse()) {
    const l = d.layers[id]!
    if (l.kind !== 'text') continue
    const r = docStore.textRect(id)
    if (r && doc.x >= r.x && doc.x <= r.x + r.w && doc.y >= r.y && doc.y <= r.y + r.h) {
      docStore.setActiveLayers([id])
      startTextEdit(id)
      return
    }
  }
  const style = { ...ui.textStyle, hex: ui.foreground }
  const id = docStore.addTextLayer({ x: Math.round(doc.x), y: Math.round(doc.y) }, '', style)
  startTextEdit(id)
}
watch(
  () => ui.activeTool,
  tool => {
    if (tool !== 'text' && ui.editingTextId) finishTextEdit()
    if (tool !== 'pen' && pen.value) finishPen(true)
    if (tool !== 'lasso' && magnet.value) finishMagnet(false)
    if (tool === 'pen' || pen.value) drawOverlay()
  }
)

// ---------- 切り抜き ----------
const crop = ref<Rect | null>(null)
function cropHandles(r: Rect): Pt[] {
  return [
    { x: r.x, y: r.y }, { x: r.x + r.w / 2, y: r.y }, { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h / 2 }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x + r.w / 2, y: r.y + r.h },
    { x: r.x, y: r.y + r.h }, { x: r.x, y: r.y + r.h / 2 }
  ]
}
function hitCrop(css: Pt): number | 'inside' | null {
  const r = crop.value
  if (!r) return null
  const hs = cropHandles(r).map(p => ui.docToCss(p))
  for (let i = 0; i < 8; i++) if (Math.abs(css.x - hs[i]!.x) <= 6 && Math.abs(css.y - hs[i]!.y) <= 6) return i
  const d = ui.cssToDoc(css)
  if (d.x >= r.x && d.x <= r.x + r.w && d.y >= r.y && d.y <= r.y + r.h) return 'inside'
  return null
}
function normalizeRect(a: Pt, b: Pt): Rect {
  const x = Math.round(Math.min(a.x, b.x)), y = Math.round(Math.min(a.y, b.y))
  return { x, y, w: Math.max(1, Math.round(Math.abs(b.x - a.x))), h: Math.max(1, Math.round(Math.abs(b.y - a.y))) }
}
function resizeRectByHandle(origin: Rect, handle: number, p: Pt): Rect {
  let x0 = origin.x, y0 = origin.y, x1 = origin.x + origin.w, y1 = origin.y + origin.h
  if ([0, 6, 7].includes(handle)) x0 = p.x
  if ([2, 3, 4].includes(handle)) x1 = p.x
  if ([0, 1, 2].includes(handle)) y0 = p.y
  if ([4, 5, 6].includes(handle)) y1 = p.y
  return normalizeRect({ x: x0, y: y0 }, { x: x1, y: y1 })
}
function commitCrop() {
  const r = crop.value
  crop.value = null
  ui.modalTool = null
  if (r) docStore.cropDocument(r)
  drawOverlay()
}
function cancelCrop() {
  crop.value = null
  ui.modalTool = null
  drawOverlay()
}
watch(
  () => ui.activeTool,
  tool => {
    if (tool !== 'crop' && crop.value) cancelCrop()
    if (tool === 'crop' && !crop.value) {
      // 選択範囲があればそれを初期値に
      const sel = docStore.selectionMask()
      if (sel) {
        crop.value = { ...sel.rect }
        ui.modalTool = 'crop'
        drawOverlay()
      }
    }
  }
)

/** 多角形選択の作業中の点 (文書座標) */
const polygon = ref<{ points: Pt[]; mode: SelectionMode; hover: Pt | null } | null>(null)
/** マグネット選択: 確定済みの経路 (文書座標)、アンカー、カーソルまでの仮経路 */
const magnet = ref<{ proxy: QuickProxy; anchors: Pt[]; path: Pt[]; hover: Pt[]; mode: SelectionMode } | null>(null)
const MAGNET_AUTO_ANCHOR_CSS = 28
function magnetSegment(from: Pt, to: Pt): Pt[] {
  const p = magnet.value!.proxy
  const seg = liveWire(p, { x: from.x / p.scale, y: from.y / p.scale }, { x: to.x / p.scale, y: to.y / p.scale })
  return simplifyPath(seg.map(q => ({ x: (q.x + 0.5) * p.scale, y: (q.y + 0.5) * p.scale })), 0.6 * p.scale)
}
function magnetAddAnchor(doc: Pt) {
  const m = magnet.value!
  const last = m.anchors[m.anchors.length - 1]!
  const seg = magnetSegment(last, doc)
  m.path.push(...seg.slice(1))
  m.anchors.push(doc)
  m.hover = []
}
function magnetHover(doc: Pt) {
  const m = magnet.value
  if (!m) return
  const last = m.anchors[m.anchors.length - 1]!
  const dCss = Math.hypot(doc.x - last.x, doc.y - last.y) * ui.view.zoom
  if (dCss >= MAGNET_AUTO_ANCHOR_CSS) magnetAddAnchor(doc)
  else m.hover = magnetSegment(last, doc)
  drawOverlay()
}
function finishMagnet(commit: boolean) {
  const m = magnet.value
  let pts: Pt[] | null = null
  if (commit && m && m.anchors.length >= 2) {
    // 閉じる経路もエッジに沿わせる (magnet.value が要るので null にする前に計算)
    const first = m.anchors[0]!, last = m.anchors[m.anchors.length - 1]!
    const closing = magnetSegment(last, first)
    pts = [...m.path, ...closing.slice(1, -1)]
  }
  magnet.value = null
  ui.modalTool = null
  if (pts && pts.length >= 3 && m) applyMask(rasterizePolygon(pts, ui.toolOptions.antialias), m.mode)
  drawOverlay()
}

function modeFromEvent(e: PointerEvent | MouseEvent): SelectionMode {
  if (e.shiftKey && e.altKey) return 'intersect'
  if (e.shiftKey) return 'add'
  if (e.altKey) return 'subtract'
  return ui.toolOptions.selectionMode
}

function applyMask(mask: MaskData | null, mode: SelectionMode) {
  const feather = ui.toolOptions.feather
  const m = mask && feather > 0 ? featherMask(mask, feather) : mask
  docStore.combineSelection(m, mode)
}

function onPointerDown(e: PointerEvent) {
  if (!docStore.doc) return
  const p = localPos(e)
  const doc = ui.cssToDoc(p)
  const tool = ui.effectiveTool
  const isPan = tool === 'hand' || e.button === 1
  const guideHit = tool === 'move' && e.button === 0 ? hitGuide(p) : null
  if (isPan) {
    drag = { kind: 'pan', last: p }
  } else if (guideHit) {
    drag = { kind: 'guide', id: guideHit.id, axis: guideHit.axis }
    docStore.beginTransaction('history.moveGuide')
  } else if (xform.value && e.button === 0) {
    const x = xform.value
    if (x.warp) {
      const wi = hitWarpPoint(p)
      if (wi !== null) {
        drag = { kind: 'xform-warp', index: wi, start: doc, origin: { ...x.warp[wi]! } }
        return
      }
      return
    }
    const hit = hitTransform(p)
    if (hit === null) return
    if (hit === 'inside') drag = { kind: 'xform-move', start: p, origin: { ...x.t } }
    else if (x.quad && typeof hit === 'number') {
      drag = { kind: 'xform-corner', corner: hit / 2, start: doc, startQuad: x.quad.map(q => ({ ...q })) as [Pt, Pt, Pt, Pt] }
    } else if (hit === 'rotate') {
      const cx = x.src.x + x.src.w / 2 + x.t.x, cy = x.src.y + x.src.h / 2 + x.t.y
      drag = { kind: 'xform-rotate', startAngle: x.angle, startPointer: Math.atan2(doc.y - cy, doc.x - cx) }
    } else {
      // 反対側のハンドルを固定する
      const corners = xformCorners(x)
      const opposite = corners[(hit + 4) % 8]!
      const r = x.src
      const localPts = [
        { x: r.x, y: r.y }, { x: r.x + r.w / 2, y: r.y }, { x: r.x + r.w, y: r.y },
        { x: r.x + r.w, y: r.y + r.h / 2 }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x + r.w / 2, y: r.y + r.h },
        { x: r.x, y: r.y + r.h }, { x: r.x, y: r.y + r.h / 2 }
      ]
      drag = { kind: 'xform-scale', handle: hit, start: p, startScale: { x: x.scaleX, y: x.scaleY }, startT: { ...x.t }, anchorWorld: opposite, local: localPts[hit]! }
    }
  } else if (tool === 'crop' && e.button === 0) {
    const hit = hitCrop(p)
    if (hit === null) drag = { kind: 'crop-new', start: doc }
    else if (hit === 'inside') drag = { kind: 'crop-move', start: doc, origin: { ...crop.value! } }
    else drag = { kind: 'crop-resize', handle: hit, origin: { ...crop.value! } }
    ui.modalTool = 'crop'
  } else if (tool === 'zoom') {
    ui.zoomStep(e.altKey ? -1 : 1, p)
    return
  } else if (tool === 'move' && e.button === 0) {
    const id = docStore.doc.activeLayerIds[0]
    const any = id ? docStore.doc.layers[id] : undefined
    if (!any || (any.kind !== 'raster' && any.kind !== 'text' && any.kind !== 'shape' && any.kind !== 'smart') || any.lock.all || any.lock.position) return
    docStore.beginTransaction('history.moveLayer')
    const origin = any.kind === 'raster' ? { ...any.offset } : any.kind === 'text' ? { ...any.position } : { x: any.transform[6], y: any.transform[7] }
    drag = { kind: 'move', layerId: any.id, start: p, origin }
  } else if (tool === 'marquee' && e.button === 0) {
    drag = { kind: 'marquee', start: doc, current: doc, mode: modeFromEvent(e), shift: false }
  } else if (tool === 'lasso' && e.button === 0 && ui.toolOptions.lassoKind === 'magnetic') {
    e.preventDefault()
    if (!magnet.value) {
      const proxy = buildQuickReference()
      if (!proxy) return
      magnet.value = { proxy, anchors: [doc], path: [doc], hover: [], mode: modeFromEvent(e) }
      ui.modalTool = 'pen'
    } else {
      const first = magnet.value.anchors[0]!
      const fc = ui.docToCss(first)
      if (magnet.value.anchors.length >= 2 && Math.hypot(fc.x - p.x, fc.y - p.y) <= 8) finishMagnet(true)
      else if (e.detail >= 2) finishMagnet(true)
      else magnetAddAnchor(doc)
    }
    drawOverlay()
    return
  } else if (tool === 'lasso' && e.button === 0) {
    if (ui.toolOptions.lassoKind === 'polygon') {
      const mode = polygon.value?.mode ?? modeFromEvent(e)
      if (!polygon.value) polygon.value = { points: [doc], mode, hover: doc }
      else {
        const first = polygon.value.points[0]!
        const closeDist = 6 / ui.view.zoom
        if (polygon.value.points.length >= 3 && Math.hypot(doc.x - first.x, doc.y - first.y) <= closeDist) {
          finishPolygon(true)
        } else {
          polygon.value.points.push(doc)
        }
      }
      drawOverlay()
      return
    }
    drag = { kind: 'lasso', points: [doc], mode: modeFromEvent(e) }
  } else if (tool === 'wand' && e.button === 0) {
    runWand(doc, modeFromEvent(e))
    return
  } else if (tool === 'quickSelect' && e.button === 0) {
    const proxy = buildQuickReference()
    if (!proxy) return
    const mode = modeFromEvent(e)
    const subtract = mode === 'subtract'
    docStore.beginTransaction('history.quickSelect')
    drag = { kind: 'quick', proxy, base: mode === 'new' ? null : docStore.selectionMask(), mode: mode === 'new' ? 'add' : mode === 'intersect' ? 'intersect' : mode, subtract, lastLive: 0, last: doc }
    quickDab(drag, doc)
    quickLive(drag, true)
    return
  } else if (tool === 'eyedropper' && e.button === 0) {
    pickColor(doc, e.altKey)
    return
  } else if (tool === 'text' && e.button === 0) {
    // 既定の mousedown (フォーカス移動) を止めて、textarea へのフォーカスを保つ
    e.preventDefault()
    if (ui.editingTextId) {
      // 編集中に外側をクリック → 確定してから新規/編集
      finishTextEdit()
    }
    textToolClick(doc)
    return
  } else if (tool === 'fill' && e.button === 0) {
    runFill(doc)
    return
  } else if (tool === 'pen' && e.button === 0) {
    e.preventDefault()
    if (!pen.value) {
      const hit = hitPenEdit(p)
      if (hit) {
        const l = penEditTarget()!
        if (l.geometry.type !== 'path') return
        const inv = penInvert(l.transform)
        if (!inv) return
        docStore.beginTransaction('history.editPath')
        drag = { kind: 'pen-edit', layerId: l.id, index: hit.index, part: hit.part, start: p, orig: clonePoints(l.geometry.points), local: penApply(inv, doc) }
        return
      }
      startPen(doc)
      drag = { kind: 'pen-new', index: 0, start: p }
      drawOverlay()
      return
    }
    const first = pen.value.points[0]!
    const fc = ui.docToCss(first)
    if (pen.value.points.length >= 2 && Math.hypot(fc.x - p.x, fc.y - p.y) <= 8) {
      pen.value.closed = true
      finishPen(true)
      return
    }
    if (e.detail >= 2) {
      finishPen(true)
      return
    }
    pen.value.points.push({ x: doc.x, y: doc.y })
    drag = { kind: 'pen-new', index: pen.value.points.length - 1, start: p }
    drawOverlay()
  } else if (tool === 'shape' && e.button === 0) {
    drag = { kind: 'shape', start: doc, current: doc, shift: e.shiftKey }
  } else if (tool === 'gradient' && e.button === 0) {
    drag = { kind: 'gradient', start: doc, current: doc }
  } else if ((tool === 'brush' || tool === 'eraser') && e.button === 0 && ui.quickMask) {
    // クイックマスク: ブラシは選択範囲へ加算、消しゴム (または Alt) は減算
    const st = tool === 'eraser' ? ui.eraser : ui.brush
    const subtract = tool === 'eraser' || e.altKey
    const ok = beginStroke({ layerId: '', color: [0, 0, 0], opacity: 1, flow: st.flow, size: st.size, hardness: st.pencil ? 1 : st.hardness, spacing: Math.max(0.01, st.spacing), smoothing: st.smoothing, mode: subtract ? 'erase' : 'paint', pencil: st.pencil, pressureSize: st.pressureSize, pressureFlow: st.pressureFlow, lockTransparency: false }, true)
    if (!ok) return
    drag = { kind: 'stroke', label: 'history.quickMask', quickMask: { subtract, opacity: st.opacity } }
    addStrokeSample({ x: doc.x, y: doc.y, pressure: pressureOf(e) })
  } else if ((tool === 'brush' || tool === 'eraser') && e.button === 0) {
    const label = startBrushStroke(tool, e)
    if (!label) return
    drag = { kind: 'stroke', label }
    addStrokeSample({ x: doc.x, y: doc.y, pressure: pressureOf(e) })
  } else if (tool === 'smudge' && e.button === 0) {
    const layer = activeRasterForPaint()
    if (!layer) return
    drag = { kind: 'smudge', layerId: layer.id, samples: [{ x: doc.x, y: doc.y, pressure: pressureOf(e) }] }
    return
  } else if (ui.isRetouchTool(tool) && e.button === 0) {
    if ((tool === 'clone' || tool === 'heal') && e.altKey) {
      ui.cloneSource = { x: doc.x, y: doc.y }
      ui.cloneOffset = null
      return
    }
    const label = startRetouchStroke(tool, doc)
    if (!label) return
    drag = { kind: 'stroke', label, ...(tool === 'spotHeal' ? { heal: 'spot' as const } : tool === 'heal' ? { heal: 'brush' as const } : {}) }
    addStrokeSample({ x: doc.x, y: doc.y, pressure: pressureOf(e) })
  } else {
    return
  }
  dragging.value = true
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  e.preventDefault()
}

function onPointerMove(e: PointerEvent) {
  const p = localPos(e)
  const doc = docStore.doc ? ui.cssToDoc(p) : null
  ui.cursorDocPos = doc
  if (polygon.value && doc) {
    polygon.value.hover = doc
    drawOverlay()
  }
  if (drag?.kind === 'stroke' || ['brush', 'eraser', 'quickSelect'].includes(ui.effectiveTool) || ui.isRetouchTool()) drawOverlay()
  if (pen.value && !drag && doc) {
    pen.value.hover = doc
    drawOverlay()
  }
  if (magnet.value && !drag && doc) magnetHover(doc)
  if (!drag || !doc) return
  if (drag.kind === 'pen-new' && pen.value) {
    const pt = pen.value.points[drag.index]
    if (pt && Math.hypot(p.x - drag.start.x, p.y - drag.start.y) > 2) {
      pt.out = { x: doc.x, y: doc.y }
      pt.in = { x: pt.x - (doc.x - pt.x), y: pt.y - (doc.y - pt.y) }
    }
    drawOverlay()
    return
  }
  if (drag.kind === 'pen-edit') {
    penEditMove(drag, doc, e.altKey)
    drawOverlay()
    return
  }
  if (drag.kind === 'smudge') {
    drag.samples.push({ x: doc.x, y: doc.y, pressure: pressureOf(e) })
    drawOverlay()
    return
  }
  if (drag.kind === 'quick') {
    // 間を補間して撫でる
    const step = Math.max(2, ui.toolOptions.quickSize / 3)
    const dist = Math.hypot(doc.x - drag.last.x, doc.y - drag.last.y)
    const n = Math.max(1, Math.ceil(dist / step))
    for (let i = 1; i <= n; i++) quickDab(drag, { x: drag.last.x + ((doc.x - drag.last.x) * i) / n, y: drag.last.y + ((doc.y - drag.last.y) * i) / n })
    drag.last = doc
    quickLive(drag, false)
    drawOverlay()
    return
  }
  if (drag.kind === 'stroke') {
    const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e]
    for (const ce of events.length ? events : [e]) {
      const cp = ui.cssToDoc(localPos(ce))
      addStrokeSample({ x: cp.x, y: cp.y, pressure: pressureOf(ce) })
    }
    return
  }
  if (drag.kind === 'pan') {
    ui.panBy(p.x - drag.last.x, p.y - drag.last.y)
    drag.last = p
  } else if (drag.kind === 'xform-move' && xform.value) {
    const z = ui.view.zoom
    const nt = { x: drag.origin.x + (p.x - drag.start.x) / z, y: drag.origin.y + (p.y - drag.start.y) / z }
    if (xform.value.quad) {
      const ddx = nt.x - xform.value.t.x, ddy = nt.y - xform.value.t.y
      xform.value.quad = xform.value.quad.map(q => ({ x: q.x + ddx, y: q.y + ddy })) as [Pt, Pt, Pt, Pt]
    }
    xform.value.t = nt
    pushLiveTransform()
  } else if (drag.kind === 'xform-rotate' && xform.value) {
    const x = xform.value
    const cx = x.src.x + x.src.w / 2 + x.t.x, cy = x.src.y + x.src.h / 2 + x.t.y
    let a = drag.startAngle + Math.atan2(doc.y - cy, doc.x - cx) - drag.startPointer
    if (e.shiftKey) a = Math.round(a / (Math.PI / 12)) * (Math.PI / 12)
    x.angle = a
    pushLiveTransform()
  } else if (drag.kind === 'xform-warp' && xform.value && xform.value.warp) {
    const x = xform.value
    const d = drag
    const warp = x.warp ?? []
    const np = { x: d.origin.x + (doc.x - d.start.x), y: d.origin.y + (doc.y - d.start.y) }
    x.warp = warp.map((q, i) => (i === d.index ? np : q))
    pushLiveTransform()
  } else if (drag.kind === 'xform-corner' && xform.value && xform.value.quad) {
    const x = xform.value
    const dx = doc.x - drag.start.x, dy = doc.y - drag.start.y
    const q = drag.startQuad.map(pt => ({ ...pt })) as [Pt, Pt, Pt, Pt]
    const k = drag.corner
    q[k] = { x: q[k]!.x + dx, y: q[k]!.y + dy }
    if (x.mode === 'perspective') {
      // 同じ辺の隣の隅を鏡像に動かす (台形になる)
      const hPartner = k === 0 ? 1 : k === 1 ? 0 : k === 2 ? 3 : 2
      const vPartner = k === 0 ? 3 : k === 1 ? 2 : k === 2 ? 1 : 0
      q[hPartner] = { x: q[hPartner]!.x - dx, y: q[hPartner]!.y }
      q[vPartner] = { x: q[vPartner]!.x, y: q[vPartner]!.y - dy }
    }
    x.quad = q
    pushLiveTransform()
  } else if (drag.kind === 'xform-scale' && xform.value) {
    const x = xform.value
    const r = x.src
    const cx = r.x + r.w / 2, cy = r.y + r.h / 2
    // ポインタを回転前のローカル座標へ (中心基準)
    const c = Math.cos(-x.angle), sn = Math.sin(-x.angle)
    const wx = doc.x - (cx + x.t.x), wy = doc.y - (cy + x.t.y)
    const lx = c * wx - sn * wy, ly = sn * wx + c * wy
    const hx = drag.local.x - cx, hy = drag.local.y - cy
    let sx = x.scaleX, sy = x.scaleY
    if (hx !== 0) sx = lx / hx
    if (hy !== 0) sy = ly / hy
    if (e.shiftKey || (hx !== 0 && hy !== 0 && !e.altKey)) {
      // 角ハンドルは既定で縦横比を保つ
      const k = Math.abs(hx) >= Math.abs(hy) ? sx / drag.startScale.x : sy / drag.startScale.y
      sx = drag.startScale.x * k
      sy = drag.startScale.y * k
    }
    if (!Number.isFinite(sx) || Math.abs(sx) < 0.001) sx = x.scaleX
    if (!Number.isFinite(sy) || Math.abs(sy) < 0.001) sy = x.scaleY
    x.scaleX = sx
    x.scaleY = sy
    if (!e.altKey) {
      // 反対側のハンドルを固定: 変形後の位置と元の位置の差だけ平行移動を補正
      const after = xformCorners(x)[(drag.handle + 4) % 8]!
      x.t = { x: x.t.x + (drag.anchorWorld.x - after.x), y: x.t.y + (drag.anchorWorld.y - after.y) }
    }
    pushLiveTransform()
  } else if (drag.kind === 'crop-new') {
    crop.value = normalizeRect(drag.start, doc)
    drawOverlay()
  } else if (drag.kind === 'crop-move') {
    crop.value = { ...drag.origin, x: Math.round(drag.origin.x + doc.x - drag.start.x), y: Math.round(drag.origin.y + doc.y - drag.start.y) }
    drawOverlay()
  } else if (drag.kind === 'crop-resize') {
    crop.value = resizeRectByHandle(drag.origin, drag.handle, doc)
    drawOverlay()
  } else if (drag.kind === 'guide') {
    docStore.moveGuide(drag.id, drag.axis === 'x' ? doc.x : doc.y)
  } else if (drag.kind === 'move') {
    const z = ui.view.zoom
    let nx = drag.origin.x + (p.x - drag.start.x) / z, ny = drag.origin.y + (p.y - drag.start.y) / z
    if (ui.snapEnabled && !e.altKey && docStore.doc) {
      // レイヤー矩形をガイド / 文書端 / グリッドに吸着
      const cur = docStore.layerRect(drag.layerId)
      if (cur) {
        const l = docStore.doc.layers[drag.layerId]
        const curOrigin = l?.kind === 'raster' ? l.offset : l?.kind === 'text' ? l.position : l?.kind === 'shape' || l?.kind === 'smart' ? { x: l.transform[6], y: l.transform[7] } : { x: 0, y: 0 }
        const rect = { x: cur.x + (nx - curOrigin.x), y: cur.y + (ny - curOrigin.y), w: cur.w, h: cur.h }
        const targets = snapTargets(docStore.doc.meta.width, docStore.doc.meta.height, ui.showGuides ? docStore.doc.guides : [], ui.showGrid ? ui.gridSize : null)
        const d = snapDelta(rect, targets, 8 / z)
        nx += d.dx
        ny += d.dy
      }
    }
    docStore.setLayerOffset(drag.layerId, { x: nx, y: ny })
  } else if (drag.kind === 'shape') {
    drag.current = doc
    drag.shift = e.shiftKey
    drawOverlay()
  } else if (drag.kind === 'gradient') {
    drag.current = doc
    if (e.shiftKey) {
      // 45° 単位に拘束
      const dx = doc.x - drag.start.x, dy = doc.y - drag.start.y
      const ang = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4)
      const len = Math.hypot(dx, dy)
      drag.current = { x: drag.start.x + Math.cos(ang) * len, y: drag.start.y + Math.sin(ang) * len }
    }
    drawOverlay()
  } else if (drag.kind === 'marquee') {
    drag.current = doc
    drag.shift = e.shiftKey
    drawOverlay()
  } else if (drag.kind === 'lasso') {
    const last = drag.points[drag.points.length - 1]!
    if (Math.hypot(doc.x - last.x, doc.y - last.y) >= 1 / ui.view.zoom) drag.points.push(doc)
    drawOverlay()
  }
}

function marqueeRect(d: Extract<Drag, { kind: 'marquee' }>): Rect {
  let w = d.current.x - d.start.x
  let h = d.current.y - d.start.y
  if (d.shift) {
    const s = Math.max(Math.abs(w), Math.abs(h))
    w = Math.sign(w || 1) * s
    h = Math.sign(h || 1) * s
  }
  const x = Math.round(Math.min(d.start.x, d.start.x + w))
  const y = Math.round(Math.min(d.start.y, d.start.y + h))
  return { x, y, w: Math.round(Math.abs(w)), h: Math.round(Math.abs(h)) }
}

function onPointerUp(e: PointerEvent) {
  if (!drag) return
  const d = drag
  drag = null
  dragging.value = false
  ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
  if (d.kind === 'crop-new' && crop.value && (crop.value.w < 2 || crop.value.h < 2)) {
    crop.value = null
    ui.modalTool = null
  }
  if (d.kind === 'move') docStore.commitTransaction()
  else if (d.kind === 'guide') {
    // ビューポート外へ出したら削除
    const p = localPos(e)
    docStore.commitTransaction()
    if (p.x < 0 || p.y < 0) docStore.removeGuide(d.id)
  } else if (d.kind === 'stroke') {
    const p = ui.cssToDoc(localPos(e))
    if (e.type === 'pointercancel') cancelStroke()
    else if (d.quickMask) endQuickMaskStroke({ x: p.x, y: p.y, pressure: pressureOf(e) }, d.quickMask.subtract, d.quickMask.opacity)
    else if (d.heal) void finishHeal(d.heal, { x: p.x, y: p.y, pressure: pressureOf(e) })
    else endStroke({ x: p.x, y: p.y, pressure: pressureOf(e) }, d.label)
  } else if (d.kind === 'shape') {
    createShape(d)
  } else if (d.kind === 'pen-edit') {
    docStore.commitTransaction()
    drawOverlay()
  } else if (d.kind === 'smudge') {
    finishSmudge(d)
  } else if (d.kind === 'quick') {
    quickLive(d, true)
    docStore.commitTransaction()
    drawOverlay()
  } else if (d.kind === 'pen-new') {
    drawOverlay()
  } else if (d.kind === 'gradient') {
    runGradient(d.start, d.current)
  } else if (d.kind === 'marquee') {
    const r = marqueeRect(d)
    if (r.w < 1 || r.h < 1) {
      // クリックだけ → 新規モードなら選択解除
      if (d.mode === 'new') docStore.deselect()
    } else {
      const aa = ui.toolOptions.antialias
      applyMask(ui.toolOptions.marqueeShape === 'ellipse' ? rasterizeEllipse(r, aa) : rasterizeRect(r, aa), d.mode)
    }
  } else if (d.kind === 'lasso') {
    if (d.points.length >= 3) applyMask(rasterizePolygon(d.points, ui.toolOptions.antialias), d.mode)
    else if (d.mode === 'new') docStore.deselect()
  }
  drawOverlay()
}

function finishPolygon(commit: boolean) {
  const poly = polygon.value
  polygon.value = null
  if (commit && poly && poly.points.length >= 3) applyMask(rasterizePolygon(poly.points, ui.toolOptions.antialias), poly.mode)
  drawOverlay()
}

function onDoubleClick() {
  if (polygon.value) finishPolygon(true)
  else if (xform.value) commitTransform()
  else if (crop.value) commitCrop()
}

function onKeyDown(e: KeyboardEvent) {
  if (e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return
  if (ui.editingTextId) return
  if (xform.value) {
    if (e.key === 'Enter') { commitTransform(); e.preventDefault() }
    else if (e.key === 'Escape') { cancelTransform(); e.preventDefault() }
    return
  }
  if (crop.value) {
    if (e.key === 'Enter') { commitCrop(); e.preventDefault() }
    else if (e.key === 'Escape') { cancelCrop(); e.preventDefault() }
    return
  }
  if (magnet.value) {
    if (e.key === 'Enter') { finishMagnet(true); e.preventDefault() }
    else if (e.key === 'Escape') { finishMagnet(false); e.preventDefault() }
    else if (e.key === 'Backspace' || e.key === 'Delete') {
      const m = magnet.value
      if (m.anchors.length <= 1) finishMagnet(false)
      else {
        // 最後のアンカーまでの経路を捨てる
        m.anchors.pop()
        const prevAnchor = m.anchors[m.anchors.length - 1]!
        const idx = m.path.findIndex(q => q.x === prevAnchor.x && q.y === prevAnchor.y)
        if (idx >= 0) m.path.length = idx + 1
        m.hover = []
        drawOverlay()
      }
      e.preventDefault()
    }
    return
  }
  if (pen.value) {
    if (e.key === 'Enter') { finishPen(true); e.preventDefault() }
    else if (e.key === 'Escape') { finishPen(false); e.preventDefault() }
    else if (e.key === 'Backspace' || e.key === 'Delete') {
      pen.value.points.pop()
      if (!pen.value.points.length) finishPen(false)
      else drawOverlay()
      e.preventDefault()
    }
    return
  }
  if (!polygon.value) return
  if (e.key === 'Enter') {
    finishPolygon(true)
    e.preventDefault()
  } else if (e.key === 'Escape') {
    finishPolygon(false)
    e.preventDefault()
  } else if (e.key === 'Backspace' || e.key === 'Delete') {
    polygon.value.points.pop()
    if (polygon.value.points.length === 0) polygon.value = null
    drawOverlay()
    e.preventDefault()
  }
}

function pressureOf(e: PointerEvent): number {
  // マウスは pressure 0.5 固定 → 筆圧なし (1.0) として扱う
  return e.pointerType === 'mouse' || e.pressure === 0 ? 1 : e.pressure
}

/** ブラシ / 消しゴムのストロークを開始する。戻り値は履歴ラベル (開始できなければ null) */
function startBrushStroke(tool: 'brush' | 'eraser', e: PointerEvent): string | null {
  const d = docStore.doc!
  const id = d.activeLayerIds[0]
  const layer = id ? docStore.rasterLayer(id) : undefined
  if (!layer) {
    toast.warn(t('brush.needRasterLayer'))
    return null
  }
  if (layer.lock.all || layer.lock.pixels) {
    toast.warn(t('brush.locked'))
    return null
  }
  const st = tool === 'eraser' ? ui.eraser : ui.brush
  const erase = tool === 'eraser' && !layer.lock.transparency
  // 透明ロック中の消しゴムは背景色で塗る (Photoshop と同じ)
  const colorHex = tool === 'eraser' ? ui.background : e.altKey ? ui.background : ui.foreground
  const rgb = hexToRgb(colorHex) ?? { r: 0, g: 0, b: 0 }
  const ok = beginStroke({
    layerId: layer.id,
    color: rgbToUnit(rgb),
    opacity: st.opacity,
    flow: st.flow,
    size: st.size,
    hardness: st.pencil ? 1 : st.hardness,
    spacing: Math.max(0.01, st.spacing),
    smoothing: st.smoothing,
    mode: erase ? 'erase' : 'paint',
    pencil: st.pencil,
    pressureSize: st.pressureSize,
    pressureFlow: st.pressureFlow,
    lockTransparency: layer.lock.transparency,
    angle: st.angle,
    roundness: st.roundness,
    scatter: st.scatter,
    sizeJitter: st.sizeJitter,
    tip: useBrushStore().tipById(st.tipId)
  })
  if (!ok) return null
  return tool === 'eraser' ? 'history.eraser' : st.pencil ? 'history.pencil' : 'history.brush'
}

function activeRasterForPaint() {
  const d = docStore.doc!
  const id = d.activeLayerIds[0]
  const layer = id ? docStore.rasterLayer(id) : undefined
  if (!layer) {
    toast.warn(t('brush.needRasterLayer'))
    return null
  }
  if (layer.lock.all || layer.lock.pixels) {
    toast.warn(t('brush.locked'))
    return null
  }
  return layer
}

/** 塗りつぶし: クリック位置の同色領域 (∩ 選択範囲) を描画色で塗る */
function runFill(doc: Pt) {
  const layer = activeRasterForPaint()
  if (!layer) return
  const o = ui.toolOptions
  const d = docStore.doc!
  let rgba: Uint8ClampedArray
  let rect: Rect
  if (o.fillSampleAll) {
    const c = readComposite()
    rgba = c.rgba
    rect = { x: 0, y: 0, w: c.width, h: c.height }
  } else {
    const s = pixelStore.get(layer.pixelId)
    const b = s?.bounds()
    if (!s || !b) {
      // 空のレイヤー: 文書全体を対象にする
      rect = { x: 0, y: 0, w: d.meta.width, h: d.meta.height }
      rgba = new Uint8ClampedArray(rect.w * rect.h * 4)
    } else {
      rect = { x: b.x + layer.offset.x, y: b.y + layer.offset.y, w: b.w, h: b.h }
      rgba = s.readRect(b)
    }
  }
  let mask = magicWand(rgba, rect, doc, { tolerance: o.fillTolerance, contiguous: o.fillContiguous, antialias: o.antialias })
  if (!mask) return
  const sel = docStore.selectionMask()
  if (sel) mask = combineMasks(mask, sel, 'intersect')
  if (!mask) return
  const color = hexToRgb(ui.foreground) ?? { r: 0, g: 0, b: 0 }
  const alpha = maskToAlpha(mask, mask.rect)
  docStore.compositeIntoLayer(layer.id, mask.rect, solidWithAlpha(mask.rect, [color.r, color.g, color.b], alpha, o.fillOpacity), 'history.fill')
}

/** グラデーション: 選択範囲 (なければ文書全体) に描画色→背景色を塗る */
function runGradient(a: Pt, b: Pt) {
  const layer = activeRasterForPaint()
  if (!layer) return
  if (Math.hypot(b.x - a.x, b.y - a.y) < 1) return
  const d = docStore.doc!
  const o = ui.toolOptions
  const sel = docStore.selectionMask()
  const rect: Rect = sel ? sel.rect : { x: 0, y: 0, w: d.meta.width, h: d.meta.height }
  const fg = hexToRgb(ui.foreground) ?? { r: 0, g: 0, b: 0 }
  const bg = hexToRgb(ui.background) ?? { r: 255, g: 255, b: 255 }
  const custom = o.gradientStops
  const spec = {
    style: o.gradientStyle,
    stops: custom
      ? custom.map(s => { const c = hexToRgb(s.hex) ?? { r: 0, g: 0, b: 0 }; return { pos: s.pos, color: [c.r, c.g, c.b] as [number, number, number], alpha: s.alpha } })
      : [
          { pos: 0, color: [fg.r, fg.g, fg.b] as [number, number, number], alpha: 1 },
          { pos: 1, color: o.gradientToTransparent ? ([fg.r, fg.g, fg.b] as [number, number, number]) : ([bg.r, bg.g, bg.b] as [number, number, number]), alpha: o.gradientToTransparent ? 0 : 1 }
        ],
    reverse: o.gradientReverse,
    dither: o.gradientDither
  }
  const rgba = renderGradient(rect, a, b, spec, sel ? maskToAlpha(sel, rect) : undefined, o.gradientOpacity)
  docStore.compositeIntoLayer(layer.id, rect, rgba, 'history.gradient')
}

function shapeRectOf(d: { start: Pt; current: Pt; shift: boolean }): Rect {
  let w = d.current.x - d.start.x, h = d.current.y - d.start.y
  if (d.shift) {
    const s = Math.max(Math.abs(w), Math.abs(h))
    w = Math.sign(w || 1) * s
    h = Math.sign(h || 1) * s
  }
  return { x: Math.round(Math.min(d.start.x, d.start.x + w)), y: Math.round(Math.min(d.start.y, d.start.y + h)), w: Math.round(Math.abs(w)), h: Math.round(Math.abs(h)) }
}

function createShape(d: { start: Pt; current: Pt; shift: boolean }) {
  const o = ui.toolOptions
  const r = shapeRectOf(d)
  if (o.shapeType !== 'line' && (r.w < 1 || r.h < 1)) return
  const props = {
    fillHex: o.shapeFill ? ui.foreground : null,
    strokeHex: o.shapeStroke ? ui.background : null,
    strokeWidth: o.shapeStrokeWidth
  }
  if (o.shapeType === 'line') {
    if (Math.hypot(d.current.x - d.start.x, d.current.y - d.start.y) < 1) return
    docStore.addShapeLayer({ type: 'line', x1: Math.round(d.start.x), y1: Math.round(d.start.y), x2: Math.round(d.current.x), y2: Math.round(d.current.y) }, { ...props, strokeHex: ui.foreground, fillHex: null })
    return
  }
  if (o.shapeType === 'rect') docStore.addShapeLayer({ type: 'rect', ...r, radius: o.shapeRadius }, props)
  else if (o.shapeType === 'ellipse') docStore.addShapeLayer({ type: 'ellipse', ...r }, props)
  else docStore.addShapeLayer({ type: 'polygon', ...r, sides: o.shapeSides, star: o.shapeStar }, props)
}

/** レタッチ系ブラシ (コピースタンプ / 覆い焼き / 焼き込み / スポンジ / ぼかし / シャープ) */
function startRetouchStroke(tool: string, start: Pt): string | null {
  const layer = activeRasterForPaint()
  if (!layer) return null
  const r = ui.retouch
  let mode: 'clone' | 'dodge' | 'burn' | 'sponge' | 'desponge' | 'blur' | 'sharpen'
  let cloneOffset: Pt | undefined
  let strength = r.strength
  if (tool === 'spotHeal') {
    // ソース無しのクローン (オフセット 0) でカバレッジだけ集め、確定時に修復する
    mode = 'clone'
    cloneOffset = { x: 0, y: 0 }
  } else if (tool === 'clone' || tool === 'heal') {
    if (!ui.cloneSource) {
      toast.warn(t('clone.setSource'))
      return null
    }
    // 調整あり: 最初のストロークで決めたオフセットを保つ。なし: 毎回ソースから始める
    if (!r.cloneAligned || !ui.cloneOffset) ui.cloneOffset = { x: start.x - ui.cloneSource.x, y: start.y - ui.cloneSource.y }
    cloneOffset = ui.cloneOffset
    mode = 'clone'
  } else if (tool === 'dodge') { mode = 'dodge'; strength = r.exposure }
  else if (tool === 'burn') { mode = 'burn'; strength = r.exposure }
  else if (tool === 'sponge') { mode = r.spongeDesaturate ? 'desponge' : 'sponge'; strength = r.flow }
  else if (tool === 'blurTool') mode = 'blur'
  else mode = 'sharpen'
  const ok = beginStroke({
    layerId: layer.id,
    color: [0, 0, 0],
    opacity: 1,
    flow: tool === 'heal' || tool === 'spotHeal' ? 1 : r.flow,
    size: r.size,
    hardness: r.hardness,
    spacing: 0.1,
    smoothing: 0,
    mode,
    cloneOffset,
    strength,
    pencil: false,
    pressureSize: false,
    pressureFlow: false,
    lockTransparency: layer.lock.transparency
  })
  if (!ok) return null
  return `history.${tool}`
}

/** 修復ブラシ / スポット修復の確定: Worker でポアソン合成してから書き戻す */
async function finishHeal(kind: 'spot' | 'brush', last: StrokeSample) {
  const r = finishStrokeForHeal(last)
  if (!r) return
  const gen = docStore.doc?.pixelGeneration
  try {
    const out = kind === 'spot' ? await retouchAsync('spotHeal', r.original, r.rect.w, r.rect.h, r.coverage) : await retouchAsync('heal', r.original, r.rect.w, r.rect.h, r.coverage, r.cloned)
    if (docStore.doc?.pixelGeneration !== gen) return
    docStore.writeLayerRect(r.layerId, r.local, out, kind === 'spot' ? 'history.spotHeal' : 'history.heal')
  } catch (e) {
    toast.error(e instanceof Error ? e.message : String(e))
  }
}

/** 指先: ストローク終了時にまとめて CPU で計算して書き戻す */
function finishSmudge(d: Extract<Drag, { kind: 'smudge' }>) {
  const layer = docStore.rasterLayer(d.layerId)
  const s = layer ? pixelStore.get(layer.pixelId) : undefined
  const doc = docStore.doc
  if (!layer || !s || !doc || d.samples.length < 2) return
  const r = ui.retouch
  // 対象矩形: サンプルの外接矩形 + サイズ (文書内)
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const p of d.samples) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y) }
  const pad = Math.ceil(r.size / 2) + 2
  const rect = rectIntersect({ x: Math.floor(minX - pad), y: Math.floor(minY - pad), w: Math.ceil(maxX - minX) + pad * 2 + 1, h: Math.ceil(maxY - minY) + pad * 2 + 1 }, { x: 0, y: 0, w: doc.meta.width, h: doc.meta.height })
  if (!rect) return
  const local = { x: rect.x - layer.offset.x, y: rect.y - layer.offset.y, w: rect.w, h: rect.h }
  const img = s.readRect(local)
  const res = smudgeStroke(img, rect.w, rect.h, d.samples.map(p => ({ x: p.x - rect.x, y: p.y - rect.y, pressure: p.pressure })), { size: r.size, hardness: r.hardness, strength: r.strength })
  if (!res.rect) return
  docStore.writeLayerRect(layer.id, local, res.rgba, 'history.smudge')
}

function pickColor(doc: Pt, toBackground: boolean) {
  const px = sampleComposite(doc.x, doc.y)
  if (!px) return
  const hex = rgbToHex({ r: px[0], g: px[1], b: px[2] })
  if (toBackground) ui.background = hex
  else ui.foreground = hex
}

/** クイック選択の参照画像 (全レイヤー or アクティブレイヤー) からプロキシを作る */
function buildQuickReference(): QuickProxy | null {
  const d = docStore.doc!
  if (ui.toolOptions.quickSampleAll) {
    const c = readComposite()
    return buildQuickProxy(c.rgba, c.width, c.height)
  }
  const id = d.activeLayerIds[0]
  const layer = id ? docStore.rasterLayer(id) : undefined
  if (!layer) return null
  const s = pixelStore.get(layer.pixelId)
  if (!s) return null
  // レイヤーを文書矩形に展開
  const rgba = s.readRect({ x: -layer.offset.x, y: -layer.offset.y, w: d.meta.width, h: d.meta.height })
  return buildQuickProxy(rgba, d.meta.width, d.meta.height)
}
function quickDab(d: Extract<Drag, { kind: 'quick' }>, doc: Pt) {
  const p = d.proxy
  quickSelectDab(p, { x: doc.x / p.scale, y: doc.y / p.scale, radius: ui.toolOptions.quickSize / 2 / p.scale, tolerance: ui.toolOptions.quickTolerance, edgeThreshold: 48, subtract: d.subtract })
}
/** 蓄積したマスクを選択範囲へ反映 (大きな文書では間引く) */
function quickLive(d: Extract<Drag, { kind: 'quick' }>, force: boolean) {
  const doc = docStore.doc!
  const now = performance.now()
  const big = doc.meta.width * doc.meta.height > 4_000_000
  if (!force && (big || now - d.lastLive < 80)) return
  d.lastLive = now
  const full = quickMaskToFull(d.proxy, doc.meta.width, doc.meta.height)
  if (d.subtract) {
    // 減算: 元の選択から蓄積 (0 になった部分) を引く → 蓄積は「残す領域」ではなく「消す領域」として扱う
    const erase = full
    if (!d.base) return
    const next = erase ? combineMasks(d.base, erase, 'subtract') : d.base
    docStore.setSelection(next, 'history.quickSelect')
    return
  }
  const next = d.base ? combineMasks(d.base, full ?? { rect: { x: 0, y: 0, w: 1, h: 1 }, data: new Uint8ClampedArray(1) }, d.mode) : full
  docStore.setSelection(next, 'history.quickSelect')
}

function runWand(doc: Pt, mode: SelectionMode) {
  const d = docStore.doc!
  const o = ui.toolOptions
  let rgba: Uint8ClampedArray
  let rect: Rect
  if (o.wandSampleAll) {
    const c = readComposite()
    rgba = c.rgba
    rect = { x: 0, y: 0, w: c.width, h: c.height }
  } else {
    const id = d.activeLayerIds[0]
    const layer = id ? docStore.rasterLayer(id) : undefined
    if (!layer) return
    const s = pixelStore.get(layer.pixelId)
    const b = s?.bounds()
    if (!s || !b) return
    rect = { x: b.x + layer.offset.x, y: b.y + layer.offset.y, w: b.w, h: b.h }
    rgba = s.readRect(b)
  }
  const gen = d.pixelGeneration
  void magicWandAsync(rgba, rect, doc, { tolerance: o.wandTolerance, contiguous: o.wandContiguous, antialias: o.antialias }).then(mask => {
    if (docStore.doc?.pixelGeneration !== gen) return
    docStore.combineSelection(mask, mode)
  })
}

function onPointerLeave() {
  ui.cursorDocPos = null
  drawOverlay()
}

function onWheel(e: WheelEvent) {
  if (!docStore.doc) return
  e.preventDefault()
  const p = localPos(e)
  if (e.ctrlKey || e.metaKey) {
    const factor = Math.exp(-e.deltaY * (e.ctrlKey && !e.metaKey ? 0.01 : 0.002))
    ui.setZoomAt(ui.view.zoom * factor, p)
  } else {
    ui.panBy(-e.deltaX, -e.deltaY)
  }
}

// --- 2D オーバーレイ (ラバーバンド・なげなわ・多角形) ---
function drawOverlay() {
  const c = overlay.value
  if (!c) return
  const dpr = window.devicePixelRatio || 1
  const W = Math.round(ui.viewportSize.w * dpr)
  const H = Math.round(ui.viewportSize.h * dpr)
  if (c.width !== W || c.height !== H) {
    c.width = W
    c.height = H
  }
  const ctx = c.getContext('2d')!
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, W, H)
  const active = drag && (drag.kind === 'marquee' || drag.kind === 'lasso') ? drag : null
  const brushTool = ui.effectiveTool === 'brush' || ui.effectiveTool === 'eraser' || ui.isRetouchTool() || ui.effectiveTool === 'quickSelect'
  const grad = drag?.kind === 'gradient' ? drag : null
  const shp = drag?.kind === 'shape' ? drag : null
  const showAux = docStore.doc && (ui.showGrid || (ui.showGuides && (docStore.doc.guides.length || ui.draggingGuide)))
  const penTarget = ui.effectiveTool === 'pen' && !pen.value ? penEditTarget() : null
  if (!active && !polygon.value && !magnet.value && !grad && !shp && !xform.value && !crop.value && !showAux && !pen.value && !penTarget && !(brushTool && ui.cursorDocPos)) return
  ctx.scale(dpr, dpr)
  if (magnet.value) {
    const m = magnet.value
    const drawPts = (pts: Pt[], dash: number[]) => {
      if (pts.length < 2) return
      ctx.setLineDash(dash)
      ctx.beginPath()
      pts.forEach((q, i) => { const c = ui.docToCss(q); if (i === 0) ctx.moveTo(c.x, c.y); else ctx.lineTo(c.x, c.y) })
      ctx.stroke()
    }
    ctx.lineWidth = 1
    ctx.strokeStyle = '#000'
    drawPts(m.path, [])
    ctx.strokeStyle = '#fff'
    drawPts(m.path, [4, 4])
    ctx.strokeStyle = '#19c3f5'
    drawPts([m.anchors[m.anchors.length - 1]!, ...m.hover.slice(1)], [3, 3])
    ctx.setLineDash([])
    ctx.fillStyle = '#fff'
    ctx.strokeStyle = '#19c3f5'
    for (const a of m.anchors) { const c = ui.docToCss(a); ctx.fillRect(c.x - 2.5, c.y - 2.5, 5, 5); ctx.strokeRect(c.x - 3, c.y - 3, 6, 6) }
  }
  const drawPenPath = (pts: PenPoint[], closed: boolean, toCss: (p: Pt) => Pt, hover: Pt | null) => {
    ctx.lineWidth = 1
    ctx.strokeStyle = '#19c3f5'
    ctx.setLineDash([])
    ctx.beginPath()
    pts.forEach((pt, i) => {
      const q = toCss(pt)
      if (i === 0) ctx.moveTo(q.x, q.y)
      else {
        const prev = pts[i - 1]!
        const c1 = toCss(prev.out ?? prev), c2 = toCss(pt.in ?? pt)
        ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, q.x, q.y)
      }
    })
    if (closed && pts.length > 1) {
      const last = pts[pts.length - 1]!, first = pts[0]!
      const c1 = toCss(last.out ?? last), c2 = toCss(first.in ?? first), q = toCss(first)
      ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, q.x, q.y)
    }
    ctx.stroke()
    if (hover && pts.length) {
      const last = pts[pts.length - 1]!
      const a = toCss(last), c1 = toCss(last.out ?? last), h = toCss(hover)
      ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.bezierCurveTo(c1.x, c1.y, h.x, h.y, h.x, h.y)
      ctx.stroke()
      ctx.setLineDash([])
    }
    for (const pt of pts) {
      const a = toCss(pt)
      for (const h of [pt.in, pt.out]) {
        if (!h) continue
        const q = toCss(h)
        ctx.strokeStyle = '#19c3f5'
        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        ctx.lineTo(q.x, q.y)
        ctx.stroke()
        ctx.fillStyle = '#fff'
        ctx.beginPath()
        ctx.arc(q.x, q.y, 3, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
      }
      ctx.fillStyle = '#fff'
      ctx.strokeStyle = '#19c3f5'
      ctx.fillRect(a.x - 3, a.y - 3, 6, 6)
      ctx.strokeRect(a.x - 3.5, a.y - 3.5, 7, 7)
    }
  }
  if (pen.value) drawPenPath(pen.value.points, pen.value.closed, p => ui.docToCss(p), pen.value.hover)
  if (penTarget && penTarget.geometry.type === 'path') drawPenPath(penTarget.geometry.points, penTarget.geometry.closed, p => ui.docToCss(penApply(penTarget.transform, p)), null)
  if (docStore.doc && ui.showGrid) {
    const z = ui.view.zoom
    const step = ui.gridSize * z
    if (step >= 4) {
      const o = ui.docToCss({ x: 0, y: 0 }), e = ui.docToCss({ x: docStore.doc.meta.width, y: docStore.doc.meta.height })
      ctx.strokeStyle = 'rgba(128,128,128,0.35)'
      ctx.lineWidth = 1
      ctx.beginPath()
      for (let x = o.x; x <= e.x + 0.5; x += step) { ctx.moveTo(Math.round(x) + 0.5, o.y); ctx.lineTo(Math.round(x) + 0.5, e.y) }
      for (let y = o.y; y <= e.y + 0.5; y += step) { ctx.moveTo(o.x, Math.round(y) + 0.5); ctx.lineTo(e.x, Math.round(y) + 0.5) }
      ctx.stroke()
    }
  }
  if (docStore.doc && ui.showGuides) {
    ctx.lineWidth = 1
    const drawGuide = (axis: 'x' | 'y', pos: number, color: string) => {
      ctx.strokeStyle = color
      ctx.beginPath()
      if (axis === 'x') { const x = Math.round(ui.docToCss({ x: pos, y: 0 }).x) + 0.5; ctx.moveTo(x, 0); ctx.lineTo(x, ui.viewportSize.h) }
      else { const y = Math.round(ui.docToCss({ x: 0, y: pos }).y) + 0.5; ctx.moveTo(0, y); ctx.lineTo(ui.viewportSize.w, y) }
      ctx.stroke()
    }
    for (const g of docStore.doc.guides) drawGuide(g.axis, g.position, '#19c3f5')
    if (ui.draggingGuide) drawGuide(ui.draggingGuide.axis, ui.draggingGuide.position, '#f56e19')
  }
  if (shp) {
    const a = ui.docToCss(shp.start), b = ui.docToCss(shp.current)
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(255,255,255,0.9)'
    ctx.setLineDash([4, 3])
    ctx.beginPath()
    if (ui.toolOptions.shapeType === 'line') {
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
    } else if (ui.toolOptions.shapeType === 'ellipse') {
      const r = shapeRectOf(shp)
      const p = ui.docToCss({ x: r.x, y: r.y }), q = ui.docToCss({ x: r.x + r.w, y: r.y + r.h })
      ctx.ellipse((p.x + q.x) / 2, (p.y + q.y) / 2, Math.abs(q.x - p.x) / 2, Math.abs(q.y - p.y) / 2, 0, 0, Math.PI * 2)
    } else {
      const r = shapeRectOf(shp)
      const p = ui.docToCss({ x: r.x, y: r.y }), q = ui.docToCss({ x: r.x + r.w, y: r.y + r.h })
      ctx.rect(p.x, p.y, q.x - p.x, q.y - p.y)
    }
    ctx.stroke()
    ctx.setLineDash([])
  }
  const handleBox = (p: Pt) => {
    ctx.fillStyle = '#fff'
    ctx.strokeStyle = '#000'
    ctx.setLineDash([])
    ctx.lineWidth = 1
    ctx.fillRect(p.x - 4, p.y - 4, 8, 8)
    ctx.strokeRect(p.x - 4 + 0.5, p.y - 4 + 0.5, 8, 8)
  }
  if (xform.value?.warp) {
    const ctrl = xform.value.warp
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(25,195,245,0.9)'
    ctx.setLineDash([])
    const N = 12
    ctx.beginPath()
    for (let j = 0; j <= 3; j++) {
      for (let i = 0; i <= N; i++) { const q = ui.docToCss(bezierPatch(ctrl, i / N, j / 3)); if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y) }
    }
    for (let i = 0; i <= 3; i++) {
      for (let j = 0; j <= N; j++) { const q = ui.docToCss(bezierPatch(ctrl, i / 3, j / N)); if (j === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y) }
    }
    ctx.stroke()
    for (const p of ctrl) handleBox(ui.docToCss(p))
  } else if (xform.value) {
    const corners = xformCorners(xform.value).map(p => ui.docToCss(p))
    ctx.beginPath()
    for (const i of [0, 2, 4, 6]) {
      const c = corners[i]!
      if (i === 0) ctx.moveTo(c.x, c.y)
      else ctx.lineTo(c.x, c.y)
    }
    ctx.closePath()
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(0,0,0,0.8)'
    ctx.setLineDash([])
    ctx.stroke()
    ctx.strokeStyle = 'rgba(255,255,255,0.9)'
    ctx.setLineDash([4, 4])
    ctx.stroke()
    for (const c of corners) handleBox(c)
    // 中心
    const cx = (corners[0]!.x + corners[4]!.x) / 2, cy = (corners[0]!.y + corners[4]!.y) / 2
    ctx.beginPath()
    ctx.arc(cx, cy, 4, 0, Math.PI * 2)
    ctx.strokeStyle = '#000'
    ctx.setLineDash([])
    ctx.stroke()
  }
  if (crop.value) {
    const r = crop.value
    const a = ui.docToCss({ x: r.x, y: r.y }), b = ui.docToCss({ x: r.x + r.w, y: r.y + r.h })
    // 外側を暗く
    ctx.fillStyle = 'rgba(0,0,0,0.5)'
    ctx.beginPath()
    ctx.rect(0, 0, ui.viewportSize.w, ui.viewportSize.h)
    ctx.rect(a.x, a.y, b.x - a.x, b.y - a.y)
    ctx.fill('evenodd')
    ctx.strokeStyle = '#fff'
    ctx.setLineDash([])
    ctx.lineWidth = 1
    ctx.strokeRect(Math.round(a.x) + 0.5, Math.round(a.y) + 0.5, Math.round(b.x - a.x), Math.round(b.y - a.y))
    // 三分割線
    ctx.strokeStyle = 'rgba(255,255,255,0.4)'
    for (let i = 1; i < 3; i++) {
      ctx.beginPath(); ctx.moveTo(a.x + ((b.x - a.x) * i) / 3, a.y); ctx.lineTo(a.x + ((b.x - a.x) * i) / 3, b.y); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(a.x, a.y + ((b.y - a.y) * i) / 3); ctx.lineTo(b.x, a.y + ((b.y - a.y) * i) / 3); ctx.stroke()
    }
    for (const h of cropHandles(r)) handleBox(ui.docToCss(h))
  }
  if (grad) {
    const a = ui.docToCss(grad.start), b = ui.docToCss(grad.current)
    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(b.x, b.y)
    ctx.lineWidth = 3
    ctx.strokeStyle = 'rgba(0,0,0,0.6)'
    ctx.stroke()
    ctx.lineWidth = 1
    ctx.strokeStyle = '#fff'
    ctx.stroke()
  }
  if (brushTool && ui.cursorDocPos && docStore.doc) {
    // ブラシの輪郭カーソル
    const st = ui.activeBrushSettings()
    const c = ui.docToCss(ui.cursorDocPos)
    const r = ((ui.effectiveTool === 'quickSelect' ? ui.toolOptions.quickSize : st.size) * ui.view.zoom) / 2
    ctx.beginPath()
    ctx.arc(c.x, c.y, Math.max(r, 1.5), 0, Math.PI * 2)
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(0,0,0,0.8)'
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(c.x, c.y, Math.max(r, 1.5) + 1, 0, Math.PI * 2)
    ctx.strokeStyle = 'rgba(255,255,255,0.8)'
    ctx.stroke()
    if (ui.effectiveTool === 'clone' && ui.cloneSource) {
      // コピー元の位置 (ストローク中はオフセット分ずれて追従)
      const src = drag?.kind === 'stroke' && ui.cloneOffset ? { x: ui.cursorDocPos.x - ui.cloneOffset.x, y: ui.cursorDocPos.y - ui.cloneOffset.y } : ui.cloneSource
      const s = ui.docToCss(src)
      ctx.beginPath()
      ctx.moveTo(s.x - 6, s.y); ctx.lineTo(s.x + 6, s.y); ctx.moveTo(s.x, s.y - 6); ctx.lineTo(s.x, s.y + 6)
      ctx.strokeStyle = '#000'
      ctx.stroke()
      ctx.strokeStyle = '#fff'
      ctx.setLineDash([2, 2])
      ctx.stroke()
      ctx.setLineDash([])
    }
  }
  ctx.lineWidth = 1
  const stroke = (path: () => void) => {
    path()
    ctx.strokeStyle = '#000'
    ctx.setLineDash([])
    ctx.stroke()
    ctx.strokeStyle = '#fff'
    ctx.setLineDash([4, 4])
    ctx.stroke()
  }
  if (active?.kind === 'marquee') {
    const r = marqueeRect(active)
    const a = ui.docToCss({ x: r.x, y: r.y })
    const b = ui.docToCss({ x: r.x + r.w, y: r.y + r.h })
    stroke(() => {
      ctx.beginPath()
      if (ui.toolOptions.marqueeShape === 'ellipse') ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2)
      else ctx.rect(Math.round(a.x) + 0.5, Math.round(a.y) + 0.5, Math.round(b.x - a.x), Math.round(b.y - a.y))
    })
  } else if (active?.kind === 'lasso') {
    stroke(() => {
      ctx.beginPath()
      active.points.forEach((p, i) => {
        const s = ui.docToCss(p)
        if (i === 0) ctx.moveTo(s.x, s.y)
        else ctx.lineTo(s.x, s.y)
      })
    })
  } else if (polygon.value) {
    const poly = polygon.value
    stroke(() => {
      ctx.beginPath()
      poly.points.forEach((p, i) => {
        const s = ui.docToCss(p)
        if (i === 0) ctx.moveTo(s.x, s.y)
        else ctx.lineTo(s.x, s.y)
      })
      if (poly.hover) {
        const s = ui.docToCss(poly.hover)
        ctx.lineTo(s.x, s.y)
      }
    })
    const first = ui.docToCss(poly.points[0]!)
    ctx.beginPath()
    ctx.arc(first.x, first.y, 4, 0, Math.PI * 2)
    ctx.fillStyle = '#fff'
    ctx.fill()
    ctx.strokeStyle = '#000'
    ctx.setLineDash([])
    ctx.stroke()
  }
}

function onDragOver(e: DragEvent) {
  e.preventDefault()
  dropActive.value = true
}
function onDragLeave() {
  dropActive.value = false
}
async function onDrop(e: DragEvent) {
  e.preventDefault()
  dropActive.value = false
  const files = e.dataTransfer ? [...e.dataTransfer.files] : []
  if (files.length) await openFiles(files)
}

const cursor = computed(() => {
  const tool = ui.effectiveTool
  if (xform.value) return 'move'
  if (tool === 'crop') return 'crosshair'
  if (tool === 'hand') return dragging.value ? 'grabbing' : 'grab'
  if (tool === 'zoom') return 'zoom-in'
  if (tool === 'marquee' || tool === 'lasso' || tool === 'wand') return 'crosshair'
  if (tool === 'brush' || tool === 'eraser' || ui.isRetouchTool(tool)) return 'none'
  if (tool === 'eyedropper' || tool === 'fill' || tool === 'gradient') return 'crosshair'
  if (tool === 'text') return 'text'
  if (tool === 'shape') return 'crosshair'
  return 'default'
})
</script>

<template>
  <div
    ref="host"
    class="viewport"
    :class="{ drop: dropActive }"
    :style="{ cursor }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @pointerleave="onPointerLeave"
    @dblclick="onDoubleClick"
    @wheel="onWheel"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
    @contextmenu.prevent
  >
    <div v-if="rulerSize" class="ruler corner" :style="{ width: rulerSize + 'px', height: rulerSize + 'px' }" />
    <canvas v-if="rulerSize" ref="rulerX" class="ruler top" :style="{ left: rulerSize + 'px', height: rulerSize + 'px' }" @pointerdown="onRulerDown('y', $event)" />
    <canvas v-if="rulerSize" ref="rulerY" class="ruler left" :style="{ top: rulerSize + 'px', width: rulerSize + 'px' }" @pointerdown="onRulerDown('x', $event)" />
    <div class="area" :style="{ left: rulerSize + 'px', top: rulerSize + 'px' }">
      <canvas ref="glCanvas" class="gl" />
      <canvas ref="overlay" class="overlay" />
    </div>
    <textarea
      v-if="editing"
      ref="textArea"
      class="text-edit"
      :style="{ ...textAreaStyle, marginLeft: rulerSize + 'px', marginTop: rulerSize + 'px' }"
      :value="editing.text"
      :placeholder="t('text.placeholder')"
      spellcheck="false"
      @input="onTextInput"
      @keydown="onTextKey"
      @pointerdown.stop
      @wheel.stop
    />
    <div v-if="!docStore.hasDocument" class="start">
      <h1>{{ t('start.title') }}</h1>
      <p class="muted">{{ t('start.subtitle') }}</p>
      <div class="row">
        <button class="primary" @click="ui.dialog = 'new'">{{ t('start.new') }}</button>
        <button @click="openFilePicker()">{{ t('start.open') }}</button>
      </div>
      <p class="muted drop-hint">{{ t('start.drop') }}</p>
      <p class="muted guide-line"><button class="ghost link" @click="ui.toolGuideOpen = true">{{ t('start.guide') }}</button></p>
    </div>
  </div>
</template>

<style scoped>
.viewport {
  position: relative;
  overflow: hidden;
  background: var(--canvas-bg);
  touch-action: none;
}
.viewport.drop::after {
  content: '';
  position: absolute;
  inset: 6px;
  border: 2px dashed var(--accent);
  border-radius: var(--radius-lg);
  pointer-events: none;
}
.area {
  position: absolute;
  right: 0;
  bottom: 0;
}
.gl,
.overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}
.ruler {
  position: absolute;
  background: var(--bg-2);
  z-index: 3;
}
.ruler.corner {
  left: 0;
  top: 0;
  border-right: 1px solid var(--line-strong);
  border-bottom: 1px solid var(--line-strong);
}
.ruler.top {
  top: 0;
  right: 0;
  border-bottom: 1px solid var(--line-strong);
  cursor: row-resize;
}
.ruler.left {
  left: 0;
  bottom: 0;
  border-right: 1px solid var(--line-strong);
  cursor: col-resize;
}
.overlay {
  pointer-events: none;
}
.text-edit {
  position: absolute;
  margin: 0;
  padding: 0;
  border: 1px dashed var(--accent);
  background: transparent;
  outline: none;
  resize: none;
  overflow: hidden;
  white-space: pre;
  caret-color: var(--accent);
  z-index: 5;
  /* 本体の描画はレイヤー側で行うので文字は透明にし、キャレットと選択だけ見せる */
  -webkit-text-fill-color: transparent;
}
.text-edit::selection {
  background: rgba(79, 156, 245, 0.35);
}
.start {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 20px;
}
.start h1 {
  font-size: 20px;
  margin: 0;
}
.start p {
  margin: 0;
  max-width: 420px;
}
.start .link {
  text-decoration: underline;
  padding: 0 2px;
  height: auto;
  font-size: inherit;
  color: inherit;
}
.guide-line {
  margin-top: 2px;
}
.drop-hint {
  margin-top: 18px;
  font-size: 11px;
}
</style>
